import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import { Box, Flex, useMantineTheme } from '@mantine/core';
import { runInAction } from 'mobx';
import { observer, useLocalObservable } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { SegmentExport, TSegmentExportState } from '@feathr/blackbox';
import { Button, EmptyState, Icon, Input, Table, Tooltip } from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import { useLocalUrl, useStore } from '@feathr/extender/state';

import dataExportsColumns from './dataExportsColumns';
import { useExportsPolling } from './DataExportsPage.hooks';
import type { IExportsFilters, TExportsDisplayStatus } from './ExportsFilters';
import ExportsFiltersPopover from './ExportsFiltersPopover';

const ACTIVE_STATES = new Set<TSegmentExportState>(['pending', 'running']);
const DEFAULT_PAGE_SIZE = 5;

/*
 * Maps the four display-level status pill values to the raw BE state machine
 * (5 values). "In progress" fans out to both `pending` and `running` because
 * the chip merges them; the rest are 1:1.
 */
const DISPLAY_TO_BE_STATES: Record<TExportsDisplayStatus, TSegmentExportState[]> = {
  in_progress: ['pending', 'running'],
  completed: ['ready'],
  failed: ['failed'],
  cancelled: ['stopped'],
};

function DataExportsPage(): JSX.Element {
  const { SegmentExports } = useStore();
  const { t } = useTranslation();
  const localUrl = useLocalUrl();
  const theme = useMantineTheme();

  const filterState = useLocalObservable(() => ({
    appliedFilters: {} as IExportsFilters,
  }));

  function handleApplyFilters(newFilters: IExportsFilters): void {
    runInAction(() => {
      filterState.appliedFilters = { ...newFilters };
    });
  }

  const beStates = filterState.appliedFilters.status__in?.flatMap(
    (status) => DISPLAY_TO_BE_STATES[status],
  );

  const tableFilters = {
    ...(beStates?.length ? { state__in: beStates } : {}),
    ...(filterState.appliedFilters.export_type__in?.length
      ? { export_type__in: filterState.appliedFilters.export_type__in }
      : {}),
  };

  const hasAppliedFilters =
    !!filterState.appliedFilters.status__in?.length ||
    !!filterState.appliedFilters.export_type__in?.length;

  const segmentExports = SegmentExports.list({ filters: tableFilters });
  const hasExports = segmentExports.models.length > 0;
  const hasActiveExports = segmentExports.models.some((row) => ACTIVE_STATES.has(row.get('state')));

  useExportsPolling(SegmentExports, hasActiveExports);

  const showEmptyState = !hasExports && !segmentExports.isPending && !hasAppliedFilters;
  const showFilteredEmptyState = !hasExports && !segmentExports.isPending && hasAppliedFilters;

  return (
    <Page
      collapseHeader={false}
      collapseHeaderOnScroll={true}
      description={t(
        'View export progress, download completed files, and manage past exports. Exports are started from the Groups page.',
      )}
      title={t('Exports')}
    >
      {showEmptyState ? (
        <EmptyState
          description={t('Export one to get started.')}
          height={'full'}
          label={t('No exports found.')}
          name={'data-exports-empty-state'}
          theme={'white'}
          width={'full'}
        >
          <Button link={localUrl('/data/segments')} type={'primary'}>
            {t('Go to Groups')}
          </Button>
        </EmptyState>
      ) : (
        <>
          <Flex align={'center'} gap={theme.spacing['3']} justify={'space-between'} mb={'3'}>
            <Tooltip title={t('Search by name is coming soon.')}>
              <Box w={256}>
                <Input
                  disabled={true}
                  id={'exportsSearchInput'}
                  name={'exports-search'}
                  placeholder={t('Search by name...')}
                  prefix={<Icon icon={faMagnifyingGlass} />}
                  value={''}
                />
              </Box>
            </Tooltip>
            <ExportsFiltersPopover
              filters={filterState.appliedFilters}
              onApply={handleApplyFilters}
            />
          </Flex>
          {showFilteredEmptyState ? (
            <EmptyState
              description={t('Try adjusting your search or filter settings.')}
              height={'full'}
              label={t('No results found.')}
              name={'data-exports-filtered-empty-state'}
              theme={'white'}
              width={'full'}
            />
          ) : (
            <Table<SegmentExport>
              collection={SegmentExports}
              columns={dataExportsColumns([], t)}
              filters={tableFilters}
              initialPagesize={DEFAULT_PAGE_SIZE}
              initialSort={[{ id: 'date_created', desc: true }]}
              isLoading={segmentExports.isPending}
            />
          )}
        </>
      )}
    </Page>
  );
}

export default observer(DataExportsPage);
